import React, { useState } from "react";
import useToast from "../../hooks/useToast";

const ToastTestPage = () => {
  const toast = useToast();
  const [loadingToastId, setLoadingToastId] = useState(null);

  // Test 1: All toast types with correct branding
  const testAllTypes = () => {
    toast.success(
      "Success toast - This should have gold border and checkmark icon"
    );
    setTimeout(() => {
      toast.error(
        "Error toast - This should have bright-red border and X icon"
      );
    }, 500);
    setTimeout(() => {
      toast.warning(
        "Warning toast - This should have gold border and triangle icon"
      );
    }, 1000);
    setTimeout(() => {
      toast.info(
        "Info toast - This should have medium-grey border and info icon"
      );
    }, 1500);
    setTimeout(() => {
      const id = toast.loading(
        "Loading toast - This should have gold border and spinning icon"
      );
      setLoadingToastId(id);
    }, 2000);
  };

  // Test 2: Auto-dismiss timers
  const testAutoDismissTimers = () => {
    toast.success("Success (3s) - Should dismiss in 3 seconds");
    setTimeout(() => {
      toast.error("Error (5s) - Should dismiss in 5 seconds");
    }, 500);
    setTimeout(() => {
      toast.warning("Warning (4s) - Should dismiss in 4 seconds");
    }, 1000);
    setTimeout(() => {
      toast.info("Info (4s) - Should dismiss in 4 seconds");
    }, 1500);
  };

  // Test 3: Manual dismissal
  const testManualDismissal = () => {
    toast.success("Click the X button to dismiss this toast manually", {
      duration: 0,
      dismissible: true,
    });
  };

  // Test 4: Loading toast with manual dismissal
  const testLoadingToast = () => {
    const id = toast.loading(
      'Processing... Click "Dismiss Loading" button to dismiss'
    );
    setLoadingToastId(id);
  };

  const dismissLoadingToast = () => {
    if (loadingToastId) {
      toast.dismiss(loadingToastId);
      setLoadingToastId(null);
    }
  };

  // Test 5: Multiple toasts stacking
  const testMultipleToasts = () => {
    for (let i = 1; i <= 3; i++) {
      setTimeout(() => {
        toast.info(`Toast ${i} - Check spacing between toasts (12px gap)`);
      }, i * 300);
    }
  };

  // Test 6: Toast queue (maxToasts limit)
  const testToastQueue = () => {
    for (let i = 1; i <= 7; i++) {
      setTimeout(() => {
        toast.info(`Toast ${i} - Only 5 should be visible at once`);
      }, i * 200);
    }
  };

  // Test 7: Hover pause/resume
  const testHoverPause = () => {
    toast.success("Hover over this toast to pause auto-dismiss timer", {
      duration: 5000,
    });
  };

  // Test 8: Custom durations
  const testCustomDurations = () => {
    toast.success("Custom 10s duration", { duration: 10000 });
    setTimeout(() => {
      toast.error("Custom 2s duration", { duration: 2000 });
    }, 500);
  };

  // Test 9: Non-dismissible toast
  const testNonDismissible = () => {
    toast.info("This toast has no X button (non-dismissible)", {
      duration: 5000,
      dismissible: false,
    });
  };

  // Test 10: Rapid fire toasts
  const testRapidFire = () => {
    let count = 0;
    const interval = setInterval(() => {
      count++;
      if (count <= 10) {
        toast.success(`Rapid toast #${count}`);
      } else {
        clearInterval(interval);
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-dark-grey p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-2">
          Toast System Test Page
        </h1>
        <p className="text-medium-grey mb-8">
          Comprehensive testing for the branded toast notification system
        </p>

        {/* Test Categories */}
        <div className="space-y-6">
          {/* Basic Functionality Tests */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              1. Basic Functionality
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={testAllTypes}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test All Toast Types
              </button>
              <button
                onClick={testAutoDismissTimers}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Auto-Dismiss Timers
              </button>
              <button
                onClick={testManualDismissal}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Manual Dismissal
              </button>
              <button
                onClick={testLoadingToast}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Loading Toast
              </button>
              {loadingToastId && (
                <button
                  onClick={dismissLoadingToast}
                  className="bg-bright-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
                >
                  Dismiss Loading Toast
                </button>
              )}
            </div>
          </section>

          {/* Layout & Stacking Tests */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              2. Layout & Stacking
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={testMultipleToasts}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Multiple Toasts (3)
              </button>
              <button
                onClick={testToastQueue}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Queue Limit (7 toasts)
              </button>
              <button
                onClick={testRapidFire}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Rapid Fire (10 toasts)
              </button>
            </div>
          </section>

          {/* Interaction Tests */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              3. Interaction Tests
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={testHoverPause}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Hover Pause/Resume
              </button>
              <button
                onClick={testNonDismissible}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Non-Dismissible Toast
              </button>
              <button
                onClick={testCustomDurations}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test Custom Durations
              </button>
            </div>
          </section>

          {/* Individual Toast Type Tests */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              4. Individual Toast Types
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <button
                onClick={() => toast.success("Success message!")}
                className="bg-gold text-dark-grey px-4 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Success
              </button>
              <button
                onClick={() => toast.error("Error message!")}
                className="bg-bright-red text-white px-4 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Error
              </button>
              <button
                onClick={() => toast.warning("Warning message!")}
                className="bg-gold text-dark-grey px-4 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Warning
              </button>
              <button
                onClick={() => toast.info("Info message!")}
                className="bg-medium-grey text-white px-4 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Info
              </button>
              <button
                onClick={() => {
                  const id = toast.loading("Loading...");
                  setTimeout(() => toast.dismiss(id), 3000);
                }}
                className="bg-gold text-dark-grey px-4 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Loading
              </button>
            </div>
          </section>

          {/* Accessibility Tests */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              5. Accessibility Tests
            </h2>
            <div className="space-y-4">
              <div className="text-white">
                <h3 className="font-semibold mb-2">Keyboard Navigation:</h3>
                <ul className="list-disc list-inside space-y-1 text-medium-grey">
                  <li>
                    Trigger a toast and press Tab to focus the dismiss button
                  </li>
                  <li>Press Enter or Space to dismiss the toast</li>
                  <li>Verify focus outline is visible</li>
                </ul>
              </div>
              <div className="text-white">
                <h3 className="font-semibold mb-2">Screen Reader:</h3>
                <ul className="list-disc list-inside space-y-1 text-medium-grey">
                  <li>Open browser DevTools → Accessibility panel</li>
                  <li>
                    Trigger error/warning toasts (should use role="alert")
                  </li>
                  <li>
                    Trigger success/info toasts (should use role="status")
                  </li>
                  <li>Verify aria-live and aria-atomic attributes</li>
                </ul>
              </div>
              <button
                onClick={() => {
                  toast.error(
                    'Error with role="alert" - Check accessibility panel'
                  );
                  setTimeout(() => {
                    toast.success(
                      'Success with role="status" - Check accessibility panel'
                    );
                  }, 1000);
                }}
                className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Test ARIA Roles
              </button>
            </div>
          </section>

          {/* Visual QA Checklist */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              6. Visual QA Checklist
            </h2>
            <div className="text-white space-y-2">
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check1" className="mt-1" />
                <label htmlFor="check1" className="text-medium-grey">
                  Success toast has gold (#f6a332) left border and checkmark
                  icon
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check2" className="mt-1" />
                <label htmlFor="check2" className="text-medium-grey">
                  Error toast has bright-red (#E31837) left border and X icon
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check3" className="mt-1" />
                <label htmlFor="check3" className="text-medium-grey">
                  Warning toast has gold border and triangle icon
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check4" className="mt-1" />
                <label htmlFor="check4" className="text-medium-grey">
                  Info toast has medium-grey (#505A64) border and info icon
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check5" className="mt-1" />
                <label htmlFor="check5" className="text-medium-grey">
                  Loading toast has spinning icon animation
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check6" className="mt-1" />
                <label htmlFor="check6" className="text-medium-grey">
                  Toasts have white background with shadow
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check7" className="mt-1" />
                <label htmlFor="check7" className="text-medium-grey">
                  Toasts have 10px border-radius (rounded corners)
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check8" className="mt-1" />
                <label htmlFor="check8" className="text-medium-grey">
                  Multiple toasts have 12px gap between them
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check9" className="mt-1" />
                <label htmlFor="check9" className="text-medium-grey">
                  Toasts slide in from top with fade-in animation (300ms)
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check10" className="mt-1" />
                <label htmlFor="check10" className="text-medium-grey">
                  Toasts slide out to top with fade-out animation (200ms)
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check11" className="mt-1" />
                <label htmlFor="check11" className="text-medium-grey">
                  Dismiss button (X) is visible and clickable
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check12" className="mt-1" />
                <label htmlFor="check12" className="text-medium-grey">
                  Maximum 5 toasts visible at once (queue system works)
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check13" className="mt-1" />
                <label htmlFor="check13" className="text-medium-grey">
                  Hovering over toast pauses auto-dismiss timer
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check14" className="mt-1" />
                <label htmlFor="check14" className="text-medium-grey">
                  Toasts are responsive on mobile (check at 375px width)
                </label>
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" id="check15" className="mt-1" />
                <label htmlFor="check15" className="text-medium-grey">
                  No console errors or warnings
                </label>
              </div>
            </div>
          </section>

          {/* Responsive Test */}
          <section className="bg-light-grey p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-4">
              7. Responsive Test
            </h2>
            <p className="text-medium-grey mb-4">
              Resize your browser window to mobile width (375px) and test toasts
            </p>
            <button
              onClick={() =>
                toast.info(
                  "This toast should be responsive on mobile viewports"
                )
              }
              className="bg-gold text-dark-grey px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
            >
              Test Mobile Responsive
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ToastTestPage;
